import React, { createContext, useContext, useState, useEffect } from 'react'

interface MobileContextType {
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  screenWidth: number
  screenHeight: number
  isPWA: boolean
  isIOS: boolean
  isAndroid: boolean
  hasTouchScreen: boolean
  canInstallPWA: boolean
}

const MobileContext = createContext<MobileContextType | undefined>(undefined)

export function MobileProvider({ children }: { children: React.ReactNode }) {
  const [isMobile, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)
  const [isDesktop, setIsDesktop] = useState(true)
  const [screenWidth, setScreenWidth] = useState(1024)
  const [screenHeight, setScreenHeight] = useState(768)
  const [isPWA, setIsPWA] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [isAndroid, setIsAndroid] = useState(false)
  const [hasTouchScreen, setHasTouchScreen] = useState(false)
  const [canInstallPWA, setCanInstallPWA] = useState(false)

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth
      const height = window.innerHeight

      setScreenWidth(width)
      setScreenHeight(height)

      setIsMobile(width < 768)
      setIsTablet(width >= 768 && width < 1024)
      setIsDesktop(width >= 1024)
    }

    // Detect PWA (standalone display mode)
    const isPWAMode = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true
    setIsPWA(isPWAMode)

    // Detect iOS
    const iosRegex = /iPad|iPhone|iPod/
    const isIOSDevice = iosRegex.test(navigator.userAgent)
    setIsIOS(isIOSDevice)

    // Detect Android
    const isAndroidDevice = /Android/i.test(navigator.userAgent)
    setIsAndroid(isAndroidDevice)

    // Detect touch screen capability
    const hasTouchCapability = () => {
      return (
        ('ontouchstart' in window) ||
        (navigator.maxTouchPoints > 0) ||
        ((navigator as any).msMaxTouchPoints > 0)
      )
    }
    setHasTouchScreen(hasTouchCapability())

    // Detect PWA install capability (beforeinstallprompt event)
    const handleBeforeInstallPrompt = () => {
      setCanInstallPWA(true)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    updateScreenSize()
    window.addEventListener('resize', updateScreenSize)

    return () => {
      window.removeEventListener('resize', updateScreenSize)
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }
  }, [])

  return (
    <MobileContext.Provider value={{ isMobile, isTablet, isDesktop, screenWidth, screenHeight, isPWA, isIOS, isAndroid, hasTouchScreen, canInstallPWA }}>
      {children}
    </MobileContext.Provider>
  )
}

export function useMobile() {
  const context = useContext(MobileContext)
  if (context === undefined) {
    throw new Error('useMobile must be used within a MobileProvider')
  }
  return context
}
